//#region imports
import { mergeIntervals } from "./interval.tools";
import * as NumberTools from "./number.tools";
import { toTimestamp, toTimestampOrNull, TsDate, UnixTimestamp, UnixTimestampIndeterminateInterval, valueOf, valueOfOrNull, valuesOf } from "./timestamps.tools.definitions";

//#region Validators

/**
 * @returns {boolean} true if value is invalid (string or null or NaN value)
 */
export function isInvalid(ts: UnixTimestamp | null | undefined): boolean {
    return !isValid(ts);
}

/**
 * @param ts UnixTimestamp to check
 * @returns {boolean} true if UnixTimestamp is valid (not null and not NaN value)
 */
export function isValid(ts: UnixTimestamp | null | undefined): ts is UnixTimestamp {
    return NumberTools.isInteger(ts);
}

//#endregion Validators

//#region Number and Intervals

/**
 * @returns -1 if nLeft < nRight, 1 if nLeft > nRight, 0 if nLeft == nRight
 */
export function compareAsc(nLeft: UnixTimestamp, nRight: UnixTimestamp): number {
    return NumberTools.compareAsc(valueOf(nLeft), valueOf(nRight));
}

/**
 * @returns -1 if nLeft > nRight, 1 if nLeft < nRight, 0 if nLeft == nRight
 */
export function compareDesc(nLeft: UnixTimestamp, nRight: UnixTimestamp): number {
    return NumberTools.compareDesc(valueOf(nLeft), valueOf(nRight));
}

/**
 * Find the timestamp in values that is the closest to approximate
 */
export function closest(approximate: UnixTimestamp, values: UnixTimestamp[]): UnixTimestamp | null {
    return toTimestampOrNull(NumberTools.closest(valueOf(approximate), valuesOf(values)));
}

/**
 * Returns the arithmetic mean (average) value from a number array. If one value is invalid, it will not be computed. If no instant is valid, null is returned
 * @returns {*} {(N | null)} mean of all valid number
 */
export function mean(values: UnixTimestamp[]): UnixTimestamp | null {
    return NumberTools.mean<UnixTimestamp>(values);
}

/**
 * Return the lowest of the given UnixTimestamp/TsDate
 * @param tsList List of UnixTimestamp/TsDate dates
 * @returns {UnixTimestamp | TsDate | null}
 */
export function minimum<Ts extends UnixTimestamp | TsDate = UnixTimestamp>(...tsList: (Ts | null | undefined)[]): Ts | null {
    return NumberTools.minimum<Ts>(...tsList);
}

/**
 * Returns the lowest. If one instant is invalid, will return the other.
 * @param a First UnixTimestamp to compare
 * @param b Second UnixTimestamp to compare
 * @returns {UnixTimestamp | null}
 */
export function min(a: UnixTimestamp | null | undefined, b: UnixTimestamp | null | undefined): UnixTimestamp | null {
    return NumberTools.min<UnixTimestamp>(a, b);
}

/**
 * Return the greatest of the given UnixTimestamp/TsDate
 * @param tsList List of UnixTimestamp/TsDate dates
 * @returns {UnixTimestamp | TsDate | null}
 */
export function maximum<Ts extends UnixTimestamp | TsDate = UnixTimestamp>(...tsList: (Ts | null | undefined)[]): Ts | null {
    return NumberTools.maximum<Ts>(...tsList);
}

/**
 * Returns the greatest. If one instant is invalid, will return the other.
 * @param a First UnixTimestamp to compare
 * @param b Second UnixTimestamp to compare
 * @returns {UnixTimestamp | null}
 */
export function max(a: UnixTimestamp | null | undefined, b: UnixTimestamp | null | undefined): UnixTimestamp | null {
    return NumberTools.max<UnixTimestamp>(a, b);
}

/**
 * if strict is true : if the intersection is a single instant getIntersection will return null
 * if begin or end is null we consider it is -/+ infinite
 * @returns true if intersects
 */
export function intersects(interval1Begin: UnixTimestamp | null | undefined, interval1End: UnixTimestamp | null | undefined, interval2Begin: UnixTimestamp | null | undefined, interval2End: UnixTimestamp | null | undefined, strict: boolean): boolean {
    if (strict) {
        return NumberTools.intersectStrict(valueOfOrNull(interval1Begin), valueOfOrNull(interval1End), valueOfOrNull(interval2Begin), valueOfOrNull(interval2End));
    }
    return NumberTools.intersect(valueOfOrNull(interval1Begin), valueOfOrNull(interval1End), valueOfOrNull(interval2Begin), valueOfOrNull(interval2End));
}

/** 
 * @returns true if intervalBegin <= timestamp < intervalEnd.
 * Check that timestamp is between begin (included) and end (excluded). If begin or end is null, will be handled as infinite.
 */
export function isInside(timestamp: UnixTimestamp, intervalBegin: UnixTimestamp | null | undefined, intervalEnd: UnixTimestamp | null | undefined): boolean {
    return NumberTools.isInside<UnixTimestamp>(timestamp, intervalBegin, intervalEnd);
}

/**
 * Check that timestamp is between begin (included) and end (excluded). If begin or end is null, will be handled as infinite.
 * @returns true if timestamp is in [begin, end] or [end, begin] (if includeExtremities is true)
 * @returns true if timestamp is in ]begin, end[ or ]end, begin[ (if includeExtremities is false)
 */
export function isBetween(timestamp: UnixTimestamp, intervalBegin: UnixTimestamp | null | undefined, intervalEnd: UnixTimestamp | null | undefined, includeExtremities: boolean): boolean {
    return NumberTools.isBetween<UnixTimestamp>(timestamp, intervalBegin, intervalEnd, includeExtremities);
}

/**
 * Returns true if ts is before the tsToCompare
 */
export function isBefore(ts: UnixTimestamp, tsToCompare: UnixTimestamp): boolean {
    return ts < tsToCompare;
}

/**
 * Returns true if ts is after the tsToCompare
 */
export function isAfter(ts: UnixTimestamp, tsToCompare: UnixTimestamp): boolean {
    return ts > tsToCompare;
}

/** Merges a list of timestamp intervals, handling open - ended ranges. */
export function mergeTimestampsIntervals(
    intervals: UnixTimestampIndeterminateInterval[]
): UnixTimestampIndeterminateInterval[] {

    const normalized = intervals
        .map(i => ({
            from: i.start ?? Number.NEGATIVE_INFINITY,
            to: i.finish ?? Number.POSITIVE_INFINITY
        }));

    const merged = mergeIntervals(normalized);

    return merged.map(i => ({
        start: i.from === Number.NEGATIVE_INFINITY
            ? undefined
            : toTimestamp(i.from),
        finish: i.to === Number.POSITIVE_INFINITY
            ? undefined
            : toTimestamp(i.to)
    }));
}
//#endregion Number and Intervals